// Setup › Workforce › Skill packs (`/c/{slug}/setup/skill-packs`, `…/{id}`; UX pass 1.1 moved it
// from Workforce, whose `/agents/skills` stays the members' read-only door; M4-02; behind the
// `agents` add-on). The packs the company's digital employees may run: the platform catalog's
// copies (`skill_pack.mirror`), each with its version, content hash and files. A pack's page shows
// every file's text, the full content hash (the value a run pins and the runtime verifies before
// it mounts the pack read-only), where the pack came from, and its roles: a pack is assigned to
// roles, and every DE whose seat holds one of them mounts it on its runs. Managers change the
// roles; everyone else reads. One call on the list (cursor-paged); the pack page makes two for a
// manager (the pack, and the company's roles, which are managers' only) and one otherwise.
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  InfoTip,
  Label,
  PageHeader,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@companyos/ui";
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PackageIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { type CompanyAccess, isManager } from "../company";
import { formatDate } from "../format";
import { rolesQuery } from "../queries";
import { LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { useCompany, useFeature } from "../use-feature";
import { AgentsOffBanner } from "./agents-ux";

type Summary = OperationOutput<"skillPacks.list">["data"][number];
type Detail = OperationOutput<"skillPacks.get">;

const packsKey = (co: string) => ["company", co, "skill-packs"] as const;
const packKey = (co: string, id: string) => ["company", co, "skill-packs", id] as const;

export const skillPacksQuery = (api: Api, co: string) =>
  infiniteQueryOptions({
    queryKey: packsKey(co),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.skillPacks.list({
        params: { co },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

export const skillPackQuery = (api: Api, co: string, id: string) =>
  queryOptions({
    queryKey: packKey(co, id),
    queryFn: () => api.client.skillPacks.get({ params: { co, id } }),
  });

const SCOPE_LABEL: Record<Summary["scope"], string> = {
  platform: "Platform",
  agency: "Agency",
  company: "Company",
};

const shortHash = (h: string) => `${h.slice(0, 12)}…`;
const bytesText = (n: number) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KiB`);

/**
 * The add-on is off: the one Agents banner (off, and who turns it on) instead of calling routes
 * that answer 403.
 */
function AgentsOff({ company }: { company: CompanyAccess }) {
  return <AgentsOffBanner company={company} className="" />;
}

/**
 * UX pass 1.1: the packs' home is Setup › Workforce › Skill packs (managers); everyone else keeps
 * reading them at the old Workforce URL, which no sidebar lists (a member never opens Setup).
 */
export function skillPacksPaths(company: CompanyAccess) {
  return isManager(company)
    ? ({ list: "/c/$slug/setup/skill-packs", pack: "/c/$slug/setup/skill-packs/$id" } as const)
    : ({ list: "/c/$slug/agents/skills", pack: "/c/$slug/agents/skills/$id" } as const);
}

/** The title, its explanation behind (i) (UX-A: no explainer line under a title). */
function Header({ title, subtitle }: { title: string; subtitle: string }) {
  const company = useCompany();
  return (
    <PageHeader
      eyebrow={isManager(company) ? "Setup" : "Team"}
      title={title}
      badge={subtitle ? <InfoTip label={`About ${title}`}>{subtitle}</InfoTip> : null}
    />
  );
}

export function SkillPacksScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const feature = useFeature("agents");
  const header = (
    <Header
      title="Skill packs"
      subtitle="Instructions your digital employees can run, each pinned by its content hash."
    />
  );
  if (!feature.enabled)
    return (
      <>
        {header}
        <AgentsOff company={company} />
      </>
    );
  return (
    <>
      {header}
      <PackList api={api} company={company} />
    </>
  );
}

function PackList({ api, company }: { api: Api; company: CompanyAccess }) {
  const q = useInfiniteQuery(skillPacksQuery(api, company.id));
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  if (q.isPending) return <Loading label="Loading skill packs" rows={5} />;
  if (q.isError) return <LoadError what="Skill packs" error={q.error} />;
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={PackageIcon}
        title="No skill packs yet"
        description="The platform's packs appear here a few minutes after the add-on is enabled."
      />
    );
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Table data-slot="skill-packs">
        <TableCaption className="sr-only">Skill packs the company may use.</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Pack</TableHead>
            <TableHead scope="col">Version</TableHead>
            <TableHead scope="col" className="max-md:hidden">
              Content hash
            </TableHead>
            <TableHead scope="col" className="max-md:hidden">
              Files
            </TableHead>
            <TableHead scope="col">Roles</TableHead>
            <TableHead scope="col">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} data-skill-pack={p.key}>
              <TableHead scope="row" className="min-w-0 font-medium">
                <Link
                  to={skillPacksPaths(company).pack}
                  params={{ slug: company.slug, id: p.id }}
                  search={keepFocus}
                  className="underline-offset-4 hover:underline"
                >
                  {p.name}
                </Link>
                <span className="block max-w-[48ch] whitespace-normal text-xs font-normal text-muted-foreground">
                  {p.description}
                </span>
              </TableHead>
              <TableCell className="font-mono text-xs">{p.version}</TableCell>
              <TableCell className="max-md:hidden">
                <code className="font-mono text-xs" title={p.content_hash}>
                  {shortHash(p.content_hash)}
                </code>
              </TableCell>
              <TableCell className="max-md:hidden text-muted-foreground">
                {p.files.length}
              </TableCell>
              <TableCell>
                {p.role_ids.length ? (
                  `${p.role_ids.length} ${p.role_ids.length === 1 ? "role" : "roles"}`
                ) : (
                  <span className="text-muted-foreground">None</span>
                )}
              </TableCell>
              <TableCell>
                {p.archived_at ? (
                  <Badge variant="outline" dot>
                    Archived
                  </Badge>
                ) : (
                  <Badge variant={p.status === "published" ? "success" : "outline"} dot>
                    {p.status === "published" ? "Published" : "Deprecated"}
                  </Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LoadMore
        what="skill packs"
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
    </div>
  );
}

export function SkillPackScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const feature = useFeature("agents");
  if (!feature.enabled)
    return (
      <>
        <Header title="Skill pack" subtitle="A pack your digital employees can run." />
        <AgentsOff company={company} />
      </>
    );
  return <PackPage api={api} company={company} id={id} readOnly={feature.readOnly} />;
}

function PackPage({
  api,
  company,
  id,
  readOnly,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  readOnly: boolean;
}) {
  const q = useQuery(skillPackQuery(api, company.id, id));
  if (q.isPending)
    return (
      <>
        <Header title="Skill pack" subtitle="Loading…" />
        <Loading label="Loading the skill pack" rows={6} />
      </>
    );
  if (q.isError)
    return (
      <>
        <Header title="Skill pack" subtitle="A pack your digital employees can run." />
        <LoadError what="The skill pack" error={q.error} />
      </>
    );
  const p = q.data;
  const m = p.manifest;
  return (
    <div data-skill-pack-page={p.key}>
      <Header title={p.name} subtitle={p.description} />
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">{SCOPE_LABEL[p.scope]}</Badge>
        <Badge variant={p.status === "published" ? "success" : "outline"} dot>
          {p.status === "published" ? "Published" : "Deprecated"}
        </Badge>
        {p.archived_at ? (
          <Badge variant="outline" dot>
            Archived {formatDate(p.archived_at)}
          </Badge>
        ) : null}
        <Badge variant="outline">
          {m.invocation === "model" ? "Runs when the DE needs it" : "Runs when asked"}
        </Badge>
        {isManager(company) && !readOnly && p.status === "published" ? (
          <PackArchive api={api} company={company} pack={p} />
        ) : null}
        <Link
          to={skillPacksPaths(company).list}
          params={{ slug: company.slug }}
          search={keepFocus}
          className="text-sm text-muted-foreground underline-offset-4 hover:underline"
        >
          All skill packs
        </Link>
      </div>

      <Section id="pack-identity" title="Version and content hash">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Key</dt>
          <dd className="font-mono text-xs">{p.key}</dd>
          <dt className="text-muted-foreground">Version</dt>
          <dd className="font-mono text-xs">{p.version}</dd>
          <dt className="text-muted-foreground">Content hash</dt>
          <dd className="min-w-0 break-all font-mono text-xs" data-slot="content-hash">
            {p.content_hash}
          </dd>
          <dt className="text-muted-foreground">Published</dt>
          <dd>{formatDate(p.published_at)}</dd>
          {m.reads_layers.length ? (
            <>
              <dt className="text-muted-foreground">Reads</dt>
              <dd>{m.reads_layers.join(", ")}</dd>
            </>
          ) : null}
          {m.writes_layers.length ? (
            <>
              <dt className="text-muted-foreground">Writes</dt>
              <dd>{m.writes_layers.join(", ")}</dd>
            </>
          ) : null}
          {m.requires_connections.length ? (
            <>
              <dt className="text-muted-foreground">Needs</dt>
              <dd>{m.requires_connections.join(", ")}</dd>
            </>
          ) : null}
          <dt className="text-muted-foreground">Source</dt>
          <dd className="min-w-0">
            {p.source_ref ? (
              <>
                Adapted from <span className="break-all">{p.source_ref.upstream_url}</span> (
                {p.source_ref.license_spdx}, {p.source_ref.copyright}).
              </>
            ) : (
              "Written for CompanyOS."
            )}
          </dd>
        </dl>
        <p className="text-xs text-muted-foreground">
          A run pins this hash; the runtime checks the files against it before mounting the pack
          read-only, so a changed pack is a new version.
        </p>
      </Section>

      <PackRoles
        api={api}
        company={company}
        pack={p}
        canEdit={isManager(company) && !readOnly && p.status === "published"}
      />

      <Section
        id="pack-files"
        title="Files"
        description={`${p.files.length} files, ${bytesText(p.files.reduce((n, f) => n + f.bytes, 0))}`}
      >
        <div className="flex min-w-0 flex-col gap-2">
          {p.files.map((f) => (
            <details
              key={f.path}
              open={f.path === "SKILL.md"}
              data-pack-file={f.path}
              className="min-w-0 rounded-md border border-border"
            >
              <summary className="flex cursor-pointer flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-mono text-xs">{f.path}</span>
                <span className="font-mono text-2xs text-muted-foreground" title={f.sha256}>
                  {bytesText(f.bytes)} · {shortHash(f.sha256)}
                </span>
              </summary>
              <pre className="max-h-[32rem] overflow-auto border-t border-border bg-muted/40 px-3 py-2 text-xs whitespace-pre-wrap break-words">
                {f.text}
              </pre>
            </details>
          ))}
        </div>
      </Section>
    </div>
  );
}

/**
 * AGENT-SAFETY: archive (kept, no longer mounted on new runs) or restore. The unused-pack job
 * archives the same way when the company sets `settings.skills.archive_unused_after_days`.
 */
function PackArchive({ api, company, pack }: { api: Api; company: CompanyAccess; pack: Detail }) {
  const qc = useQueryClient();
  const archived = pack.archived_at !== null;
  const save = useMutation({
    mutationFn: () =>
      api.client.skillPacks.update({
        params: { co: company.id, id: pack.id },
        body: { archived: !archived },
      }),
    onSuccess: (next) => {
      qc.setQueryData(packKey(company.id, pack.id), next);
      void qc.invalidateQueries({ queryKey: packsKey(company.id), exact: true });
      toast.add({ title: archived ? "Pack restored" : "Pack archived", type: "success" });
    },
    onError: (e) => toast.add({ title: problemText(e, "Nothing was changed."), type: "error" }),
  });
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={save.isPending}
      onClick={() => save.mutate()}
      title={
        archived
          ? "Mount this pack on new runs again"
          : "Keep the pack but stop mounting it on new runs"
      }
      data-pack-archive={archived ? "restore" : "archive"}
    >
      {archived ? "Restore" : "Archive"}
    </Button>
  );
}

function PackRoles({
  api,
  company,
  pack,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  pack: Detail;
  canEdit: boolean;
}) {
  // Role names come from `roles.list`, which is managers' (a reader sees how many roles).
  const manager = isManager(company);
  const roles = useInfiniteQuery({ ...rolesQuery(api, company.id), enabled: manager });
  const allRoles = roles.data?.pages.flatMap((p) => p.data) ?? [];
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string[]>(pack.role_ids);
  useEffect(() => setPicked(pack.role_ids), [pack.role_ids]);
  const dirty =
    picked.length !== pack.role_ids.length || picked.some((r) => !pack.role_ids.includes(r));
  const save = useMutation({
    mutationFn: () =>
      api.client.skillPacks.update({
        params: { co: company.id, id: pack.id },
        body: { role_ids: picked },
      }),
    onSuccess: (next) => {
      qc.setQueryData(packKey(company.id, pack.id), next);
      void qc.invalidateQueries({ queryKey: packsKey(company.id), exact: true });
      toast.add({ title: "Roles saved", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The roles were not saved."), type: "error" }),
  });
  const nameOf = (id: string) => allRoles.find((r) => r.id === id)?.name ?? "Unknown role";
  const baseId = useId();
  return (
    <Section
      id="pack-roles"
      title="Roles and digital employees"
      description="Every DE whose seat holds one of these roles mounts this pack on its runs."
      actions={
        canEdit ? (
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save roles"}
          </Button>
        ) : null
      }
    >
      {!manager ? (
        <p className="text-sm text-muted-foreground" data-slot="pack-roles">
          {pack.role_ids.length
            ? `Assigned to ${pack.role_ids.length} ${pack.role_ids.length === 1 ? "role" : "roles"}.`
            : "Not assigned to any role."}
        </p>
      ) : roles.isPending ? (
        <Loading label="Loading roles" rows={2} />
      ) : roles.isError ? (
        <LoadError what="Roles" error={roles.error} />
      ) : canEdit ? (
        allRoles.length ? (
          <fieldset className="flex flex-col gap-2" data-slot="pack-roles">
            <legend className="sr-only">Roles that run {pack.name}</legend>
            {allRoles.map((r) => {
              const id = `${baseId}-${r.id}`;
              return (
                <div key={r.id} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={picked.includes(r.id)}
                    onCheckedChange={(v) =>
                      setPicked((cur) =>
                        v === true ? [...cur, r.id] : cur.filter((x) => x !== r.id),
                      )
                    }
                  />
                  <Label htmlFor={id}>{r.name}</Label>
                </div>
              );
            })}
            <LoadMore
              what="roles"
              hasMore={roles.hasNextPage}
              loading={roles.isFetchingNextPage}
              onMore={() => void roles.fetchNextPage()}
            />
          </fieldset>
        ) : (
          <p className="text-sm text-muted-foreground">
            The company has no roles yet: add one in Setup › Roles first.
          </p>
        )
      ) : pack.role_ids.length ? (
        <ul className="flex flex-wrap gap-2" data-slot="pack-roles">
          {pack.role_ids.map((r) => (
            <li key={r}>
              <Badge variant="outline">{nameOf(r)}</Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Not assigned to any role.</p>
      )}
      {pack.assignees.length ? (
        <ul className="flex flex-col gap-1 text-sm" data-slot="pack-assignees">
          {pack.assignees.map((a) => (
            <li key={`${a.membership_id}:${a.seat_id}`}>
              <Link
                to="/c/$slug/agents/de/$id"
                params={{ slug: company.slug, id: a.membership_id }}
                search={keepFocus}
                className="underline-offset-4 hover:underline"
              >
                {a.display_name}
              </Link>{" "}
              {manager ? (
                <span className="text-muted-foreground">as {nameOf(a.role_id)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground" data-slot="pack-assignees">
          No digital employee runs this pack yet.
        </p>
      )}
    </Section>
  );
}
